
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Destination {
  title: string;
  duration: string;
  description: string[];
  image: string;
  buttonText: string;
  buttonAction: string;
}

const destinations: Destination[] = [
  {
    title: "Sedona, Arizona (USA)",
    duration: "Around Sedona & Country Side - 7 Night / 8 Days",
    description: [
      "Known for its red rock formations, energy vortex sites, and vibrant art scene.",
      "Ideal for spiritual retreats, hiking, and wellness getaways."
    ],
    image: "/images/north-america-dest-1.jpg",
    buttonText: "Explore More",
    buttonAction: "explore"
  },
  {
    title: "Tofino, British Columbia (Canada)",
    duration: "14 Night / 15 Days",
    description: [
      "A remote surf town on Vancouver Island surrounded by rainforests and wild beaches.",
      "Perfect for eco-adventures, whale watching, and luxury lodges."
    ],
    image: "/images/north-america-dest-2.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Isla Holbox, Mexico",
    duration: "09 Night / 10 Days",
    description: [
      "A car-free island with turquoise waters, whale shark encounters, and street art charm.",
      "Great for laid-back beach escapes away from Cancun crowds."
    ],
    image: "/images/north-america-dest-3.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Banff & Jasper's Icefields Parkway, Alberta (Canada)",
    duration: "10 Night / 11 Days",
    description: [
      "One of the most scenic drives in the world with glaciers, turquoise lakes, and wildlife.",
      "A niche pick for nature lovers seeking dramatic landscapes."
    ],
    image: "/images/north-america-dest-4.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Marfa, Texas (USA)",
    duration: "10 Night / 11 Days",
    description: [
      "A quirky desert town blending art, mystery, and minimalist landscapes.",
      "Famous for contemporary art installations and the unexplained “Marfa Lights."
    ],
    image: "/images/north-america-dest-5.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "San Ignacio, Belize",
    duration: "12 Night / 13 Days",
    description: [
      "A gateway to Mayan ruins, jungle caves, and eco-lodges.",
      "Offers adventure tourism with a sustainable and cultural twist."
    ],
    image: "/images/north-america-dest-6.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Great Smoky Mountains, Tennessee/North Carolina (USA)",
    duration: "14 Night / 15 Days",
    description: [
      "Misty ridges, rich biodiversity, and Appalachian culture.",
      "Perfect for hiking, music, and hidden cabin retreats"
    ],
    image: "/images/north-america-dest-7.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "St. John, U.S. Virgin Islands",
    duration: "09 Night / 10 Days",
    description: [
      "An island where two-thirds are national park land with unspoiled beaches and reefs.",
      "A niche Caribbean escape for snorkeling and eco-luxury."
    ],
    image: "/images/north-america-dest-8.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Québec's Charlevoix Region (Canada)",
    duration: "09 Night / 10 Days",
    description: [
      "Rolling hills, farm-to-table cuisine, and riverfront villages along the St. Lawrence.",
      "Known for boutique inns, whale watching, and culinary experiences."
    ],
    image: "/images/north-america-dest-9.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  }
];

export const DestinationsSection: React.FC = () => {
  const navigate = useNavigate();

  const handleButtonClick = (action: string, title: string) => {
    if (action === 'enquire') {
      navigate(`/contact?subject=${encodeURIComponent('Enquiry about ' + title)}`);
      return;
    }

    if (action === 'explore') {
      navigate('/group-tours');
      return;
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            All of Europe's Most Popular Country Side
          </h2>
        </div>

        <div className="space-y-16">
          {destinations.map((destination, index) => (
            <div
              key={index}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
            >
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                  {destination.title}
                </h3>
                <p className="text-lg text-emerald-600 font-medium">
                  {destination.duration}
                </p>
                <ul className="space-y-3">
                  {destination.description.map((desc, descIndex) => (
                    <li key={descIndex} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-700 leading-relaxed">{desc}</p>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleButtonClick(destination.buttonAction, destination.title)}
                  className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {destination.buttonText}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center ml-2"></i>
                </button>
              </div>

              <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <img
                  src={destination.image}
                  alt={destination.title}
                  className="w-full h-80 object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
